import { ChangeEvent, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { faChevronUp, faChevronDown, faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppConstants } from "../../configs";
import { useFunctionalityCalls, useFunctionalityDepartment, useFunctionalityLocation } from "../../pages/hooks";
import { UtilsJQuery } from "../../utils";
type Props = {
  custom1Name: string | "";
  custom1Value: string | "";
  custom2Name: string | "";
  custom2Value: string | "";
  custom3Name: string | "";
  custom3Value: string | "";
  custom4Name: string | "";
  custom4Value: string | "";
  departmentName?: string;
  departmentValue?: string;
  locationName?: string;
  locationValue?: string;
  companyId?: string;
  callId?: string;
  userRole?: string;
  onRefreshSummary?: () => void | Promise<void>;
  isOpen?: boolean;
  onToggle?: () => void;
};
export function CallSummaryCustomField(props: Props): JSX.Element {
  const {
    custom1Name,
    custom1Value,
    custom2Name,
    custom2Value,
    custom3Name,
    custom3Value,
    custom4Name,
    custom4Value,
    departmentName,
    departmentValue,
    locationName,
    locationValue,
    companyId,
    callId,
    userRole,
    onRefreshSummary,
    isOpen = false,
    onToggle,
  } = props;
  const { onUpdateIntakesAfterFinish } = useFunctionalityCalls();
  const isAdmin = userRole === AppConstants.UserRoles.Admin;
  const hasAnyFieldName = Boolean(
    custom1Name ||
    custom2Name ||
    custom3Name ||
    custom4Name ||
    departmentName ||
    locationName
  );
  const normalizeSelectValue = (value?: string | null) => {
    if (value === null || value === undefined) {
      return "Any";
    }
    return value;
  };
  const displayValue = (value?: string | null) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    return value;
  };
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [requiredErrors, setRequiredErrors] = useState<Record<string, boolean>>({});
  const closeTimeoutRef = useRef<number | undefined>(undefined);
  const collapseId = "custom-input-collapse";
  const submitButtonClass = "call-summary-custom-fields-submit";
  const shouldLoadDepartmentOptions = Boolean(isEditing && companyId && departmentName);
  const shouldLoadLocationOptions = Boolean(isEditing && companyId && locationName);
  const { optionMappedDepartmentList } = useFunctionalityDepartment({
    companyId: companyId ?? "",
    shouldDepartmentGetAllInvoke: shouldLoadDepartmentOptions,
  });
  const { optionMappedLocationList } = useFunctionalityLocation({
    companyId: companyId ?? "",
    shouldLocationGetAllInvoke: shouldLoadLocationOptions,
  });
  const updatedOptionMappedDepartmentList = [
    { value: "", label: "Any" },
    ...optionMappedDepartmentList,
  ];
  const updatedOptionMappedLocationList = [
    { value: "", label: "Any" },
    ...optionMappedLocationList,
  ];
  const getSelectedOption = (
    options: Array<{ value: string; label: string }>,
    value?: string | null,
    allowAny?: boolean
  ) => {
    if ((value === "" || value === null || value === undefined) && !allowAny) {
      return null;
    }
    return options.find((option) => option.label === value || option.value === value) ?? null;
  };
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "calc(1.5em + .5rem + 5px)",
      height: "calc(1.5em + .5rem + 5px)",
      fontSize: "0.875rem",
      boxShadow: "none",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 .5rem",
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: "calc(1.5em + .5rem + 2px)",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };
  const hasValue = (value?: string | null) =>
    typeof value === "string" && value.trim().length > 0;

  const [formValues, setFormValues] = useState({
    custom1Value: custom1Value ?? "",
    custom2Value: custom2Value ?? "",
    custom3Value: custom3Value ?? "",
    custom4Value: custom4Value ?? "",
    departmentValue: normalizeSelectValue(departmentValue),
    locationValue: normalizeSelectValue(locationValue),
  });
    const isFormComplete =
    (!custom1Name || hasValue(formValues.custom1Value)) &&
    (!custom2Name || hasValue(formValues.custom2Value)) &&
    (!custom3Name || hasValue(formValues.custom3Value)) &&
    (!custom4Name || hasValue(formValues.custom4Value)) &&
    (!departmentName || hasValue(formValues.departmentValue)) &&
    (!locationName || hasValue(formValues.locationValue));

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setFormValues({
      custom1Value: custom1Value ?? "",
      custom2Value: custom2Value ?? "",
      custom3Value: custom3Value ?? "",
      custom4Value: custom4Value ?? "",
      departmentValue: normalizeSelectValue(departmentValue),
      locationValue: normalizeSelectValue(locationValue),
    });
  }, [custom1Value, custom2Value, custom3Value, custom4Value, departmentValue, locationValue]);

  const startEdit = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    setFormValues({
      custom1Value: custom1Value ?? "",
      custom2Value: custom2Value ?? "",
      custom3Value: custom3Value ?? "",
      custom4Value: custom4Value ?? "",
      departmentValue: normalizeSelectValue(departmentValue),
      locationValue: normalizeSelectValue(locationValue),
    });
    setIsEditing(true);
    setEditPanelOpen(false);
    window.requestAnimationFrame(() => setEditPanelOpen(true));
    if (!isOpen) {
      onToggle?.();
    }
  };

  const onValueChange =
    (
      key:
        | "custom1Value"
        | "custom2Value"
        | "custom3Value"
        | "custom4Value"
        | "departmentValue"
        | "locationValue"
    ) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
        setRequiredErrors((prev) => ({ ...prev, [key]: false }));
      };

  const closeEditPanel = () => {
    setEditPanelOpen(false);
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsEditing(false);
    }, 200);
  };

  const onSubmit = async () => {
    if (!callId) {
      console.warn("Missing callId for intake update.");
      return;
    }
    if (!isFormComplete) {
      setRequiredErrors({
        custom1Value: Boolean(custom1Name && !hasValue(formValues.custom1Value)),
        custom2Value: Boolean(custom2Name && !hasValue(formValues.custom2Value)),
        custom3Value: Boolean(custom3Name && !hasValue(formValues.custom3Value)),
        custom4Value: Boolean(custom4Name && !hasValue(formValues.custom4Value)),
        departmentValue: Boolean(departmentName && !hasValue(formValues.departmentValue)),
        locationValue: Boolean(locationName && !hasValue(formValues.locationValue)),
      });
      return;
    }
    setRequiredErrors({});
    const btnLoading = UtilsJQuery.Ladda(`.${submitButtonClass}`);
    btnLoading.start?.();
    setIsSaving(true);
    try {
      await onUpdateIntakesAfterFinish(callId, {
        customInputRequestModels: [
          { criteriaType: 4, value: formValues.custom1Value },
          { criteriaType: 5, value: formValues.custom2Value },
          { criteriaType: 6, value: formValues.custom3Value },
          { criteriaType: 7, value: formValues.custom4Value },
          { criteriaType: 8, value: formValues.departmentValue },
          { criteriaType: 9, value: formValues.locationValue },
        ].filter(
          (entry) =>
            entry.value !== null &&
            entry.value !== undefined &&
            entry.value !== ""
        ),
      });
      await onRefreshSummary?.();
      closeEditPanel();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
      btnLoading.stop?.();
    }
  };

  const onCancel = () => {
    setFormValues({
      custom1Value: custom1Value ?? "",
      custom2Value: custom2Value ?? "",
      custom3Value: custom3Value ?? "",
      custom4Value: custom4Value ?? "",
      departmentValue: normalizeSelectValue(departmentValue),
      locationValue: normalizeSelectValue(locationValue),
    });
    setRequiredErrors({});
    closeEditPanel();
  };
  return (
    <>
      <div className="card">
        <div
          className="card-header px-0 py-1 d-flex align-items-center position-relative"
          id="headingCustomInput"
        >
          <button
            className={`btn btn-link text-start text-decoration-none text-site siteAccordion flex-grow-1${isOpen ? "" : " collapsed"}`}
            type="button"
            aria-expanded={isOpen}
            aria-controls={collapseId}
            onClick={onToggle}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="font-weight-bold">Custom Fields Input </span>
              </div>
              <div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="closed-panel-icon"
                />
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className="open-panel-icon"
                />
              </div>
            </div>
          </button>
          <div className="position-absolute" style={{right: "34px"}}>
          {isAdmin ? (
            <button
              type="button"
              className={`btn btn-link text-site text-decoration-none px-2 ${hasAnyFieldName ? "" : "text-muted"}`}
              disabled={!hasAnyFieldName}
              aria-disabled={!hasAnyFieldName}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                startEdit();
              }}
            >
              <FontAwesomeIcon icon={faEdit} className="me-1" />
              Edit
            </button>
          ) : null}
          </div>
        </div>
        <div
          className={`collapse${isOpen ? " show" : ""}`}
          id={collapseId}
          aria-labelledby="headingCustomInput"
        >
          <div className="card-body px-3 py-2">
            {isAdmin && isEditing ? (
              <div
                className="border rounded p-2 mb-3 bg-light call-summary-custom-fields-edit"
                style={{
                  transformOrigin: "top",
                  transform: editPanelOpen ? "scaleY(1)" : "scaleY(0.98)",
                  opacity: editPanelOpen ? 1 : 0,
                  marginTop: editPanelOpen ? "0" : "-6px",
                  overflow: "visible",
                  transition: "transform 240ms ease, opacity 200ms ease, margin-top 200ms ease",
                  willChange: "transform, opacity",
                }}
              >
                <div className="row">
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    {custom1Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom1Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom1Value}
                          onChange={onValueChange("custom1Value")}
                        />
                        {requiredErrors.custom1Value ? (
                          <small className="text-danger">This field is required</small>
                        ) : null}
                      </div>
                    ) : null}
                    {custom2Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom2Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom2Value}
                          onChange={onValueChange("custom2Value")}
                        />
                        {requiredErrors.custom2Value ? (
                          <small className="text-danger">This field is required</small>
                        ) : null}
                      </div>
                    ) : null}
                    {custom3Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom3Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom3Value}
                          onChange={onValueChange("custom3Value")}
                        />
                        {requiredErrors.custom3Value ? (
                          <small className="text-danger">This field is required</small>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 border-sm-top">
                    {custom4Name ? (
                      <div className="form-group mb-2">
                        <label className="mb-1">{custom4Name}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formValues.custom4Value}
                          onChange={onValueChange("custom4Value")}
                        />
                        {requiredErrors.custom4Value ? (
                          <small className="text-danger">This field is required</small>
                        ) : null}
                      </div>
                    ) : null}
                    {departmentName ? (
                      <div className="form-group mb-3">
                        <label className="mb-1">{departmentName}</label>
                        <Select
                          className="p-0"
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          options={updatedOptionMappedDepartmentList}
                          placeholder={`Select ${departmentName}`}
                          value={getSelectedOption(
                            updatedOptionMappedDepartmentList,
                            formValues.departmentValue,
                            departmentValue === null || departmentValue === undefined
                          )}
                          styles={selectStyles}
                          onChange={(option) => {
                            setFormValues((prev) => ({
                              ...prev,
                              departmentValue: option?.label ?? "",
                            }));
                            setRequiredErrors((prev) => ({ ...prev, departmentValue: false }));
                          }}
                        />
                        {requiredErrors.departmentValue ? (
                          <small className="text-danger">This field is required</small>
                        ) : null}
                      </div>
                    ) : null}
                    {locationName ? (
                      <div className="form-group mb-3">
                        <label className="mb-1">{locationName}</label>
                        <Select
                          className="p-0"
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          options={updatedOptionMappedLocationList}
                          placeholder={`Select ${locationName}`}
                          value={getSelectedOption(
                            updatedOptionMappedLocationList,
                            formValues.locationValue,
                            locationValue === null || locationValue === undefined
                          )}
                          styles={selectStyles}
                          onChange={(option) => {
                            setFormValues((prev) => ({
                              ...prev,
                              locationValue: option?.label ?? "",
                            }));
                            setRequiredErrors((prev) => ({ ...prev, locationValue: false }));
                          }}
                        />
                        {requiredErrors.locationValue ? (
                          <small className="text-danger">This field is required</small>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm me-2"
                    onClick={onCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn btn-primary btn-sm ${submitButtonClass}`}
                    onClick={onSubmit}
                    disabled={isSaving}
                  >
                    Submit
                  </button>
                </div>
              </div>
            ) : null}
            <div className="row mt-1">
              {!hasAnyFieldName ? (
                <div className="col-12 text-muted">There are no entries</div>
              ) : null}
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                {custom1Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom1Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom1Value)}
                    </div>
                  </div>
                ) : null}
                {custom2Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom2Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom2Value)}
                    </div>
                  </div>
                ) : null}
                {custom3Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom3Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom3Value)}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 border-sm-top">
                {custom4Name ? (
                  <div className="row mb-2">
                    <div className="col-6">{custom4Name}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(custom4Value)}
                    </div>
                  </div>
                ) : null}
                {departmentName ? (
                  <div className="row mb-2">
                    <div className="col-6">{departmentName}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(departmentValue)}
                    </div>
                  </div>
                ) : null}
                {locationName ? (
                  <div className="row mb-2">
                    <div className="col-6">{locationName}:</div>
                    <div className="col-6 text-muted">
                      {displayValue(locationValue)}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
